import React, { useState, useEffect } from 'react';
import * as RadixPopover from '@radix-ui/react-popover';
import { Check, ChevronDown, Search } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const EMPTY_VALUE = '__EMPTY_VALUE__';

// Helper to recursively find and list all select option items from children tree
const extractItems = (children) => {
  const items = [];
  React.Children.forEach(children, (child) => {
    if (!child) return;
    if (child.props && child.props.value !== undefined) {
      items.push({
        value: child.props.value,
        label: typeof child.props.children === 'string' ? child.props.children : String(child.props.children || ''),
        disabled: child.props.disabled,
        element: child,
      });
    } else if (child.props && child.props.children) {
      items.push(...extractItems(child.props.children));
    }
  });
  return items;
};

export function Select({
  value,
  onChange,
  className,
  disabled,
  required,
  name,
  children,
  placeholder = 'Select...',
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset search query when popover closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  const selectedValue = value === '' || value === undefined || value === null ? EMPTY_VALUE : String(value);

  const handleSelect = (val) => {
    if (onChange) {
      onChange({ target: { value: val === EMPTY_VALUE ? '' : val, name } });
    }
    setOpen(false);
  };

  // Find the selected option's label to show on the trigger button
  const getSelectedLabel = () => {
    const items = extractItems(children);
    const selectedItem = items.find(
      (item) => (item.value === '' ? EMPTY_VALUE : String(item.value)) === selectedValue
    );
    return selectedItem ? selectedItem.label : placeholder;
  };

  // Scan items to count matches for showing "no results" state
  const items = extractItems(children);
  const filteredItemsCount = items.filter((item) => {
    const labelText = typeof item.label === 'string' ? item.label : String(item.label || '');
    return labelText.toLowerCase().includes(searchQuery.toLowerCase());
  }).length;

  // Filter children rendering based on search input
  const renderChildren = (nodes) => {
    return React.Children.map(nodes, (child) => {
      if (!child) return null;

      // If it's a SelectItem
      if (child.props && child.props.value !== undefined) {
        const itemVal = child.props.value === '' ? EMPTY_VALUE : String(child.props.value);
        const isSelected = selectedValue === itemVal;

        const labelText = typeof child.props.children === 'string'
          ? child.props.children
          : String(child.props.children || '');

        if (
          searchQuery &&
          !labelText.toLowerCase().includes(searchQuery.toLowerCase())
        ) {
          return null;
        }

        return React.cloneElement(child, {
          isSelected,
          onSelect: handleSelect,
        });
      }

      // If it's a nested child (fragment, group header, div, etc)
      if (child.props && child.props.children) {
        const clonedChildren = renderChildren(child.props.children);
        const hasVisibleChildren = React.Children.toArray(clonedChildren).some(
          (c) => c !== null
        );
        if (!hasVisibleChildren) return null;

        return React.cloneElement(child, {
          children: clonedChildren,
        });
      }

      return child;
    });
  };

  return (
    <RadixPopover.Root open={open} onOpenChange={setOpen}>
      <RadixPopover.Trigger
        disabled={disabled}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-left',
          className
        )}
      >
        <span className="truncate">{getSelectedLabel()}</span>
        <ChevronDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
      </RadixPopover.Trigger>

      <RadixPopover.Portal>
        <RadixPopover.Content
          className={cn(
            'relative z-50 flex flex-col max-h-96 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-md',
            'bg-background border-border w-[var(--radix-popover-trigger-width)] min-w-[12rem]',
            'animate-in fade-in zoom-in-95 duration-100'
          )}
          align="start"
          sideOffset={4}
          avoidCollisions={true}
        >
          {/* Search Bar */}
          <div className="relative border-b border-border flex items-center px-3 bg-muted/20 shrink-0">
            <Search className="w-4 h-4 text-muted-foreground mr-2 shrink-0" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-2 bg-transparent text-sm text-foreground focus:outline-none placeholder:text-muted-foreground/60"
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-xs text-muted-foreground hover:text-foreground ml-1 p-0.5 cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>

          {/* Options Viewport */}
          <div className="flex-1 overflow-y-auto p-1 space-y-0.5">
            {renderChildren(children)}
            {filteredItemsCount === 0 && (
              <div className="py-6 text-center text-xs text-muted-foreground italic">
                No results found
              </div>
            )}
          </div>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

/**
 * Visible heading for a set of related SelectItems.
 *
 * Use this instead of a native <optgroup>: this Select is a Radix popover, not
 * a real <select>, so an <optgroup> renders its `label` attribute invisibly and
 * the grouping silently disappears.
 */
export function SelectGroup({ label, children }) {
  return (
    <div className="pt-1 first:pt-0">
      <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

export const SelectItem = React.forwardRef(
  ({ className, children, value, disabled, isSelected, onSelect, ...props }, ref) => {
    return (
      <button
        type="button"
        ref={ref}
        role="option"
        aria-selected={isSelected}
        disabled={disabled}
        onClick={() => {
          if (!disabled && onSelect) {
            onSelect(value);
          }
        }}
        className={cn(
          'relative flex w-full cursor-default select-none items-center rounded-lg py-2 pl-8 pr-2 text-sm outline-none transition-colors duration-150 text-left border-none bg-transparent',
          disabled
            ? 'pointer-events-none opacity-50'
            : 'cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground text-foreground',
          isSelected ? 'bg-accent/40 font-medium' : '',
          className
        )}
        {...props}
      >
        <span className="absolute left-2.5 flex h-3.5 w-3.5 items-center justify-center">
          {isSelected && <Check className="h-4 w-4 text-primary" />}
        </span>
        <span className="truncate">{children}</span>
      </button>
    );
  }
);
SelectItem.displayName = 'SelectItem';
